import { useState, useEffect, useRef } from 'react';
import { Delete } from 'lucide-react';
import { fmtCurrency } from '../../lib/format.js';

// Sale-NumPad — wiederverwendbare Betrags-Eingabe (Quick-Sale, Spontan, Bundle).
// Sofort offenes Ziffernfeld mit Cent-Eingabe (Komma). "pristine": der erste
// Ziffern-/Komma-Tipp überschreibt den vorbelegten Wert. Reiner Controlled-Baustein:
// kein eigenes Sheet-Chrome, kein Toast.

// Sonderzeichen via Code-Point, damit kein hardcodiertes Euro-Zeichen im Quelltext steht.
const MINUS = String.fromCharCode(0x2212); // echtes Minus-Zeichen
const NBSP = String.fromCharCode(0x00a0);  // geschütztes Leerzeichen
const EUR = String.fromCharCode(0x20ac);   // Euro-Zeichen

// number -> editierbarer Roh-String ("15" | "12,5")
function numToRaw(n) {
  if (n == null || !Number.isFinite(Number(n))) return '0';
  const num = Number(n);
  if (Number.isInteger(num)) return String(num);
  return String(num).replace('.', ',');
}

function rawToNum(raw) {
  const v = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(v) ? v : 0;
}

// hideDisplay: Betrag/Caption-Block ausblenden (Wert läuft extern mit, z.B. Preis-Kalkulator).
// allowComma=false: Komma-Taste inaktiv → reine Ganzzahl-Eingabe (z.B. Stückzahl).
// Beide additiv — Default = bisheriges Verhalten (Quick-Sale, Spontan, Bundle unverändert).
// tone="sale": Live-Verkauf (Overnight-Redesign) — größerer Betrag in SF Rounded, kontraststärkere
// Tasten, 44pt-Chips, optionaler `note`-Slot unter dem Betrag (Differenz-/Ersparnis-Banner).
// Default-Ton unverändert (Preis-Kalkulator, Standgebühr-Pads).
export function SaleNumpad({ prefill = 0, wishPrice = null, caption, onValueChange, hideDisplay = false, allowComma = true, tone = 'default', note = null }) {
  const [raw, setRaw] = useState(() => numToRaw(prefill));
  const [pristine, setPristine] = useState(true);

  // Bei neuem prefill (anderes Item) neu vorbelegen
  useEffect(() => {
    setRaw(numToRaw(prefill));
    setPristine(true);
  }, [prefill]);

  const value = rawToNum(raw);

  // onValueChange nur wenn sich der Wert ändert (Callback-Ref gegen Render-Loops)
  const cbRef = useRef(onValueChange);
  useEffect(() => { cbRef.current = onValueChange; });
  useEffect(() => { cbRef.current?.(value); }, [value]);

  const pressDigit = (d) => {
    setRaw(prev => {
      if (pristine) return d; // erster Tipp überschreibt komplett
      if (prev.includes(',')) {
        const dec = prev.split(',')[1] || '';
        if (dec.length >= 2) return prev; // max 2 Nachkommastellen
      }
      if (prev === '0') return d; // keine führende Null
      return prev + d;
    });
    setPristine(false);
  };

  const pressComma = () => {
    setRaw(prev => {
      const base = pristine ? (prev || '0') : prev;
      if (base.includes(',')) return base;
      return (base === '' ? '0' : base) + ',';
    });
    setPristine(false);
  };

  const pressBackspace = () => {
    setRaw(prev => (pristine ? '' : prev.slice(0, -1)));
    setPristine(false);
  };

  const resetToWish = () => {
    setRaw(numToRaw(wishPrice));
    setPristine(true);
  };

  const subtract = (amount) => {
    setRaw(numToRaw(Math.max(0, value - amount)));
    setPristine(false);
  };

  const showChips = wishPrice != null;

  const sale = tone === 'sale';
  const keyClass = sale
    ? 'num flex h-[52px] items-center justify-center rounded-[12px] text-[26px] font-medium ' +
      'bg-[var(--fill-tertiary)] text-[var(--label-primary)] transition-[transform,background-color] duration-100 ' +
      'active:scale-[0.96] active:bg-[var(--fill-primary)] select-none'
    : 'flex items-center justify-center rounded-2xl py-4 text-2xl font-medium ' +
      'bg-secondary/60 text-foreground transition-transform duration-100 active:scale-[0.95]';
  const ghostKeyClass = sale
    ? 'flex h-[52px] items-center justify-center rounded-[12px] text-[22px] text-[var(--label-secondary)] ' +
      'transition-[transform,background-color] duration-100 active:scale-[0.96] active:bg-[var(--fill-quaternary)] select-none'
    : 'flex items-center justify-center rounded-2xl py-4 text-xl text-muted-foreground ' +
      'transition-transform duration-100 active:scale-[0.95]';
  const chipClass = sale
    ? 'h-11 rounded-full bg-[var(--fill-tertiary)] px-4 text-[15px] font-semibold transition-[transform,background-color] duration-100 active:scale-[0.96] active:bg-[var(--fill-primary)]'
    : 'rounded-full bg-secondary/60 px-4 py-2 text-sm font-semibold transition-transform duration-100 active:scale-[0.96]';

  return (
    <div data-no-sheet-drag="">
      {/* Betrag */}
      {!hideDisplay && (sale ? (
        <div className="pb-3 pt-1 text-center">
          {caption && (
            <div className="text-[13px] font-medium text-[var(--label-secondary)]">{caption}</div>
          )}
          <div data-testid="sale-amount" className="num mt-0.5 text-[52px] font-bold leading-none tracking-[-0.02em] text-[var(--label-primary)]">
            {fmtCurrency(value)}
          </div>
          <div className="mt-2 flex h-7 items-center justify-center">{note}</div>
        </div>
      ) : (
        <div className="text-center pt-1 pb-3">
          {caption && (
            <div className="text-xs font-medium text-muted-foreground">{caption}</div>
          )}
          <div className="mt-1 text-[44px] leading-none font-bold tabular-nums">
            {fmtCurrency(value)}
          </div>
        </div>
      ))}

      {/* Quick-Chips (nur mit Wunschpreis) */}
      {showChips && (
        <div className={`flex justify-center gap-2 ${sale ? 'mb-2.5' : 'mb-3'}`}>
          <button
            type="button"
            onClick={resetToWish}
            className={`${chipClass} text-[var(--accent-troedel)]`}
          >
            Wunschpreis
          </button>
          <button
            type="button"
            onClick={() => subtract(1)}
            className={`${chipClass} text-foreground`}
          >
            {`${MINUS}${NBSP}1${NBSP}${EUR}`}
          </button>
          <button
            type="button"
            onClick={() => subtract(5)}
            className={`${chipClass} text-foreground`}
          >
            {`${MINUS}${NBSP}5${NBSP}${EUR}`}
          </button>
        </div>
      )}

      {/* Ziffernfeld */}
      <div className={`grid grid-cols-3 ${sale ? 'gap-2' : 'gap-2.5'}`}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
          <button key={d} type="button" onClick={() => pressDigit(d)} className={keyClass}>
            {d}
          </button>
        ))}
        <button
          type="button"
          onClick={allowComma ? pressComma : undefined}
          disabled={!allowComma}
          className={allowComma ? keyClass : ghostKeyClass}
          aria-label="Komma"
        >
          ,
        </button>
        <button type="button" onClick={() => pressDigit('0')} className={keyClass}>
          0
        </button>
        <button type="button" onClick={pressBackspace} className={ghostKeyClass} aria-label="Löschen">
          <Delete className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
